import { IS_ENCRYPTION_ENABLED } from 'src/config';
import { getEncryptParams } from 'src/keycloak';
import { api } from 'src/services/api';
import {
  appendFormData,
  getHeaders,
  getQueryString,
  serializeErrorResponse
} from 'src/services/utils';
import { DataApiInputParams } from 'src/types/api';
import { ICreateSharepointResourceParams } from 'src/types/document';
import { getPayload } from 'src/utils/encryption';
import { blobToBuffer, decryptFileData } from 'src/utils/files/file_encryption';
import { isZstd } from 'src/utils/workers/compressEncryptPool';
import { decompressionPool } from 'src/utils/workers/decompressionPool';

export const FILE_UPLOAD_MAX_RETRIES = 5;

export const dataLibraryApi = api.injectEndpoints({
  overrideExisting: true,
  endpoints: (builder) => ({
    getDocuments: builder.query({
      async queryFn(arg, { getState }, extraOptions, fetchWithBQ) {
        const url = `files/file-details?${getQueryString(arg.params)}`;
        const result: any = await fetchWithBQ({ url, ...getHeaders(url) });
        
        if (result.error) return result;

        const normalizedData = Array.isArray(result?.data?.data)
          ? result?.data?.data.map((item) => ({
              ...item,
              attrs: (Array.isArray(item.attrs) ? item.attrs : []).reduce(
                (acc, attr) => ({ ...acc, [attr.name]: attr.value }),
                {}
              ),
              tags: Array.isArray(item.tags) ? item.tags : []
            }))
          : [];

        return {
          data: {
            ...result.data,
            data: normalizedData,
            page: arg.params?.p ?? 0,
            pageSize: arg.params?.ps ?? 10,
            pc: result.data?.pc ?? 0
          }
        };
      },
      
      serializeQueryArgs: ({ queryArgs }) => {
        const { mode, params } = queryArgs;

        if (mode === 'standard') {
          return {
            endpointName: 'getDocuments',
            mode,
            params
          };
        }

        const { p, ps, ...cleanParams } = params;
        return {
          endpointName: 'getDocuments',
          mode,
          params: cleanParams
        };
      },

      merge: (currentCache, newPage, { arg }) => {
        if (arg.mode === 'standard') {
          return newPage;
        }

        if (newPage.page === 0) {
          currentCache.data = newPage.data;
          currentCache.parent_hierarchy = newPage.parent_hierarchy;
          currentCache.current_parent_permissions =
            newPage.current_parent_permissions;
        } else {
          const existingIds = new Set(currentCache.data.map((d) => d.id));
          const newItems = newPage.data.filter((d) => !existingIds.has(d.id));
          currentCache.data.push(...newItems);
        }
        currentCache.pc = newPage.pc;
        currentCache.page = newPage.page;
      },

      // 👇 Force refetch still needed for Infinite Scroll to trigger the next page load
      forceRefetch({ currentArg, previousArg }) {
        return currentArg?.params?.p !== previousArg?.params?.p;
      },

      providesTags: (result, error, arg) => {
        const parentId = arg.params?.parent_id || 'root';

        return [
          { type: 'Files', id: 'MASTER' },
          { type: 'Files', id: parentId }
        ];
      }
    }),
    uploadDocuments: builder.mutation({
      async queryFn(
        { params }: DataApiInputParams,
        { getState },
        extraOptions,
        baseQuery
      ) {
        return baseQuery({
          url: `files/file`,
          method: 'POST',
          body: params.formData,
          ...getHeaders(`files/file`, params.formData)
        });
      }
      // invalidatesTags: ['Files'], // Handled manually in useUploadDocuments/onUploadComplete to avoid UI flickering.
    }),
    processDocuments: builder.mutation({
      async queryFn({ params }, { getState }, extraOptions, baseQuery) {
        return baseQuery({
          url: `files/file`,
          method: 'PATCH',
          body: params.formData,
          ...getHeaders(`files/file`, params.formData)
        });
      },
      invalidatesTags: ['Tags']
    }),
    updateResource: builder.mutation({
      async queryFn({ params }, { getState }, extraOptions, baseQuery) {
        let formData = appendFormData(params);
        return baseQuery({
          url: `files/folder`,
          method: 'PATCH',
          body: formData,
          ...getHeaders(`files/folder`, formData)
        });
      },
      invalidatesTags: ['Favourites', 'Share']
    }),
    createResource: builder.mutation({
      async queryFn(
        { params }: DataApiInputParams | ICreateSharepointResourceParams,
        { getState },
        extraOptions,
        baseQuery
      ) {
        let formData = appendFormData(params);
        return baseQuery({
          url: `files/folder`,
          method: 'POST',
          body: formData,
          ...getHeaders(`files/folder`, formData)
        });
      }
    }),
    addFavourite: builder.mutation({
      async queryFn(
        { params }: DataApiInputParams,
        { getState },
        extraOptions,
        baseQuery
      ) {
        let formData = appendFormData(params);
        return baseQuery({
          url: `favourites/favourite`,
          method: 'POST',
          body: formData,
          ...getHeaders(`favourites/favourite`, formData)
        });
      },
      invalidatesTags: ['Favourites']
    }),
    getFavourite: builder.query({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let url = 'favourites/favourite';
        if (IS_ENCRYPTION_ENABLED) {
          url = `${url}?payload=${btoa(getPayload('', getEncryptParams()))}`;
        }
        return fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
      },
      providesTags: ['Favourites']
    }),
    removeFavourite: builder.mutation({
      async queryFn(
        arg: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let formData = appendFormData(arg.params);
        let url = `favourites/favourite`;
        return fetchWithBQ({
          url: url,
          method: 'DELETE',
          body: formData,
          ...getHeaders(url, formData)
        });
      },
      invalidatesTags: ['Favourites']
    }),
    deleteDocument: builder.mutation({
      async queryFn(
        args: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        let formData = appendFormData(args.params);
        let url = `files/file`;

        return fetchWithBQ({
          url,
          method: 'DELETE',
          body: formData,
          ...getHeaders(url, formData)
        });
      },
      invalidatesTags: ['Favourites']
    }),
    downloadFile: builder.query({
      async queryFn(
        args: DataApiInputParams,
        { getState },
        extraOptions,
        fetchWithBQ
      ) {
        const url = `files/file?${getQueryString(args.params)}`;
        const result: any = await fetchWithBQ({
          returnUnparsedResponse: true,
          url,
          ...getHeaders(url),
          responseHandler: async (response) => {
            if (response.status === 404) {
              return {
                extended: {
                  error_name: 'REFERENCE_DOCUMENT_DELETED'
                }
              };
            } else if (response.status === 400) {
              return {
                extended: {
                  error_name: 'INSUFFICIENT_PRIVILEGES'
                }
              };
            }

            return {
              ret_val: await response.blob()
            };
          }
        });
        if (result.error) {
          return serializeErrorResponse(result.error.data);
        }
        const bufferData = (await blobToBuffer(
          result?.data?.ret_val as Blob
        )) as Buffer;
        const encoding = args.content_info?.enc as 'binary' | 'base64' | undefined;
        let decryptedBytes = await decryptFileData(bufferData, encoding);
        if (decryptedBytes && isZstd(decryptedBytes)) {
          decryptedBytes = (await 
    decompressionPool.decompress(decryptedBytes)) ?? decryptedBytes;
        }
        const decryptedData = {
          blob: decryptedBytes,
          contentType: result.meta.response.headers.get('Ea-Content-Type'),
          format: result.meta.response.headers.get('Ea-File-Format')
        };
        return { data: decryptedData };
      }
    }),
    getContentAccessRights: builder.query({
      async queryFn(arg: DataApiInputParams, _, __, fetchWithBQ) {
        const queryString = getQueryString(arg.params);
        let url = `files/folder${queryString.length ? '?' + queryString : ''}`;
        if (IS_ENCRYPTION_ENABLED) {
          url = `${url}${queryString.length ? '&' : '?'}payload=${btoa(
            getPayload('', getEncryptParams())
          )}`;
        }

        const result: any = await fetchWithBQ({
          url: url,
          ...getHeaders(url)
        });
        return result?.error ? result : result?.data;
      },
      providesTags: ['Share']
    }),
    syncProviderResource: builder.mutation({
      async queryFn({ params }, _, __, baseQuery) {
        let formData = appendFormData(params);
        return baseQuery({
          url: `files/resource-providers`,
          method: 'PATCH',
          body: formData,
          ...getHeaders(`files/resource-providers`, formData)
        });
      },
      invalidatesTags: ['Files']
    })
  })
});

export const {
  useGetDocumentsQuery,
  useGetFavouriteQuery,
  useLazyDownloadFileQuery,
  useGetContentAccessRightsQuery,
  useUploadDocumentsMutation,
  useUpdateResourceMutation,
  useCreateResourceMutation,
  useAddFavouriteMutation,
  useRemoveFavouriteMutation,
  useDeleteDocumentMutation,
  useSyncProviderResourceMutation,
  useProcessDocumentsMutation
} = dataLibraryApi;

(useUploadDocumentsMutation as any).maxRetries = FILE_UPLOAD_MAX_RETRIES;
